// Custom hooks for weather-related API calls
import { useQuery } from '@tanstack/react-query';
import { mapGdacsEvent } from '../gdacs/gdacsEvent';

/**
 * Custom hook to fetch weather events from GDACS API
 * Returns cyclones, floods, and earthquakes from the past 2 weeks
 *
 * GDACS (Global Disaster Alert and Coordination System) provides
 * real-time alerts for natural disasters worldwide.
 *
 * refetchInterval (ms | false) lets a caller poll while a live cyclone is selected.
 *
 * @returns {Object} - { events, isLoading, isError, error, refetch, dataUpdatedAt }
 */
export const useWeatherEvents = ({ refetchInterval = false } = {}) => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['gdacsEvents', 'live'],
    queryFn: async () => {
      // Get events from the past 2 weeks
      const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];

      const response = await fetch(
        `https://www.gdacs.org/gdacsapi/api/events/geteventlist/SEARCH?eventlist=TC;FL;EQ&fromdate=${twoWeeksAgo}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch GDACS events');
      }

      const data = await response.json();

      // Transform GDACS data to our format
      return data.features?.map(mapGdacsEvent) || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - events don't change frequently
    refetchInterval,
    refetchIntervalInBackground: false,
    gcTime: 60 * 60 * 1000, // 1 hour cache
    retry: 2, // Retry twice on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    events: data || [],
    isLoading,
    isError,
    error,
    refetch,
    dataUpdatedAt,
  };
};
